import {Abi, TypedDataDomain, TypedDataParameter} from 'viem';

export type SupportedNetwork =
  | 'polygon'
  | 'ethereum'
  | 'solana'
  | 'base'
  | 'tempo';

export const EXCHANGES = ['cryptoDotCom' as const];
export const EXTENSION_WALLET_PROVIDERS = [
  'MetaMask',
  'Phantom',
  'Coinbase Wallet',
] as const;

export const WALLET_PROVIDERS = [
  ...EXTENSION_WALLET_PROVIDERS,
  'WalletConnect',
] as const;

export type ExtensionWalletProvider =
  (typeof EXTENSION_WALLET_PROVIDERS)[number];

export const isExtensionWalletProvider = (
  provider: string,
): provider is ExtensionWalletProvider =>
  EXTENSION_WALLET_PROVIDERS.some(
    (extensionWalletProvider) => extensionWalletProvider === provider,
  );

export type WalletProvider = (typeof WALLET_PROVIDERS)[number];

export type Exchange = (typeof EXCHANGES)[number];

export type WalletBalance = {
  amountInMajor: number;
  amountInMinor: number;
  decimals: number;
  contractAddress: string;
};

export type Wallet = {
  id: string;
  key: string;
  address: string;
  network: SupportedNetwork;
  chainId: number;
  type: WalletProvider;
  isSmartContractWallet?: boolean;
};

export type EvmAddress = `0x${string}`;

export type EvmTransactionData =
  | {
      abi: Abi;
      address: EvmAddress;
      functionName: 'transfer';
      args: [EvmAddress, bigint];
      dataSuffix: EvmAddress;
    }
  | {
      abi: Abi;
      address: EvmAddress;
      functionName: 'transferWithMemo';
      args: [EvmAddress, bigint, EvmAddress];
    };

export type EvmNativeTransactionData = {
  to: EvmAddress;
  value: bigint;
};

export type SolanaTransactionData = {
  destinationAddress: string;
  destinationTokenAccountAddress: string | null;
  currencyContractAddress: string;
  // amount is in token minor units (USDC and USDP minor unit is 6)
  amount: bigint;
  memoMessage: string;
  latestBlockhash: string;
  walletAddress: string;
  currency: string;
};

export type EvmTypedData = {
  domain: TypedDataDomain;
  types: Record<string, TypedDataParameter[]>;
  primaryType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message: any;
};

export type EvmReadContractData = {
  abi: Abi;
  address: EvmAddress;
  functionName: string;
  args: (bigint | EvmAddress)[];
};

export type DynamicWalletKey =
  | 'coinbase'
  | 'coinbasesolana'
  | 'metamask'
  | 'metamasksol'
  | 'phantom'
  | 'phantomevm'
  | 'walletconnect';

type DownloadLinks = {
  chromeId: string | undefined;
  firefoxId: string | undefined;
  iosId: string | undefined;
  androidId: string | undefined;
};

export type ExtensionWalletOption = {
  provider: ExtensionWalletProvider;
  name: ExtensionWalletProvider;
  isInstalledOnBrowser: boolean;
  downloadLinks: DownloadLinks;
};

export type WalletConnectOption = {
  provider: 'WalletConnect';
  name: string;
  key: string;
  iconUrl: string;
  downloadLinks: DownloadLinks;
};

export type ExchangeOption = {
  provider: 'Exchange';
  name: string;
  iconUrl: string;
  key: string;
};

export type WalletOption = ExtensionWalletOption | WalletConnectOption;

export type PaymentOption = WalletOption | ExchangeOption;

export type WalletOptionsData = {
  installedExtensionWallets: ExtensionWalletProvider[];
  walletConnectWallets: WalletConnectOption[];
  exchangeOptions: ExchangeOption[];
};

export type DynamicIframeReadyData = {
  activeWallet: Wallet | null;
  walletOptions: WalletOption[];
  exchangeOptions: ExchangeOption[];
  device: 'desktop' | 'ios' | 'android';
};

export type DynamicIframeParams = {
  livemode: boolean;
  disabledWalletProviders: readonly string[];
  enableSmartContractWalletDetection: boolean;
};

export type ExchangePaymentParams = {
  exchange: Exchange;
  address: string;
  token: string;
  chainName: string;
  network: string;
  currency: string;
  tokenAmount: number;
  merchantName: string | undefined;
  description: string | undefined;
};

export type ExchangePaymentData = {
  url: string;
  qrCode: string;
  paymentId: string;
};

export type WalletConnectMessageRegistry = {
  DYNAMIC_CONNECT_WALLET: {payload: string; response: Wallet};
  DYNAMIC_DISCONNECT_WALLET: {payload: string; response: void};
  DYNAMIC_SWITCH_WALLET: {payload: string; response: void};
  DYNAMIC_EVM_SEND_TRANSACTION: {
    payload: EvmTransactionData;
    response: `0x${string}`;
  };
  DYNAMIC_EVM_SEND_NATIVE_TRANSACTION: {
    payload: EvmNativeTransactionData;
    response: `0x${string}`;
  };
  DYNAMIC_SOLANA_SIGN_TRANSACTION: {
    payload: SolanaTransactionData;
    response: string;
  };
  DYNAMIC_HIDE_MODAL: {payload: void; response: void};
  DYNAMIC_IFRAME_READY: {payload: DynamicIframeReadyData; response: void};
  DYNAMIC_SET_ACTIVE_WALLET: {payload: Wallet | null; response: void};
  DYNAMIC_SET_CONNECTED_WALLETS: {payload: Wallet[]; response: void};
  DYNAMIC_SIGN_MESSAGE: {payload: string; response: string | undefined};
  DYNAMIC_SHOW_MODAL: {payload: void; response: void};
  DYNAMIC_SWITCH_NETWORK: {payload: number | string; response: void};
  DYNAMIC_EVM_SIGN_TYPED_DATA: {
    payload: EvmTypedData;
    response: EvmAddress | undefined;
  };
  DYNAMIC_EVM_READ_CONTRACT: {
    payload: EvmReadContractData;
    response: unknown;
  };
  DYNAMIC_WALLET_CONNECT_QR_CODE_READY: {payload: string; response: void};
  DYNAMIC_CREATE_EXCHANGE_PAYMENT: {
    payload: ExchangePaymentParams;
    response: ExchangePaymentData;
  };
};
